<template>
  <div class="stock-report-data">
    <div ref="tableContainer">
      <pagination
        v-if="numberOfItems > 0"
        :total="numberOfItems"
        :page.sync="listQuery.page"
        :limit.sync="listQuery.limit"
        @pagination="paginateList"
      />
      <el-table
        id="stockReport"
        ref="stockReport"
        v-loading="loading"
        :data="paginatedItems"
        border
        fit
        highlight-current-row
        style="width: 100%"
        :max-height="getMaxTableHeight()"
        >>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['domainPrefix']"
              class="search"
              size="mini"
              :placeholder="$t('common.domain')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('common.domain')" property="code" width="90">
            <template slot-scope="scope">
              <span>{{ scope.row.domainPrefix }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['domainProductCode']"
              class="search"
              size="mini"
              :placeholder="$t('stockCorrection.stockProductCode')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('fabric.domainProductCode')" property="code" width="110">
            <template slot-scope="scope">
              <span>{{ scope.row.code }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['description']"
              class="search"
              size="mini"
              :placeholder="$t('fabric.description')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('fabric.description')" property="description" width="200"> </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <el-table-column :label="$t('fabric.yarn')" property="yarnDescription" width="200"> </el-table-column>
        </el-table-column>
        <el-table-column :label="$t('fabric.fabricStatusOption')">
          <template slot-scope="scope">
            <span>{{ getFabricStatusOptionFromRow(scope.row.status) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('fabricStockReport.totalStock')" property="stockTotal">
          <template slot-scope="scope">
            <span>{{ scope.row.stockTotal }}</span>
          </template>
        </el-table-column>
        <el-table-column v-for="stockLocation in stockLocationsToReport" :key="stockLocation.id">
          <template slot="header" slot-scope="{}">
            {{ stockLocation.name }}
          </template>

          <el-table-column
            v-if="includeAvailableAmount"
            :label="$t('fabricStockReport.availableStock')"
            property="locationAvailableAmoun"
          >
            <template slot-scope="scope">
              <span>{{ getAvailableStockFromRow(scope.row.stockForStockLocations, stockLocation.id) || 0 }}</span>
            </template>
          </el-table-column>

          <el-table-column
            v-if="includeReservedAmount"
            :label="$t('fabricStockReport.reservedStock')"
            property="locationReservedAmount"
          >
            <template slot-scope="scope">
              <span>{{ getReservedStockFromRow(scope.row.stockForStockLocations, stockLocation.id) || 0 }}</span>
            </template>
          </el-table-column>

          <el-table-column
            v-if="includeBlockedAmount"
            :label="$t('fabricStockReport.blockedStock')"
            property="locationBlockedAmount"
          >
            <template slot-scope="scope">
              <span>{{ getBlockedStockFromRow(scope.row.stockForStockLocations, stockLocation.id) || 0 }}</span>
            </template>
          </el-table-column>

          <el-table-column
            v-if="includePositionCode"
            :label="$t('fabricStockReport.positionCode')"
            property="locationPositionCode"
          >
            <template slot-scope="scope">
              <span>{{ getPositionCodeFromRow(scope.row.stockForStockLocations, stockLocation.id) }}</span>
            </template>
          </el-table-column>
        </el-table-column>

        <el-table-column :label="$t('fabricStockReport.availableStock')" property="availableStock">
          <template slot-scope="scope">
            <span>{{ scope.row.stockAvailable }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('fabricStockReport.soldStock')" property="availableStock">
          <template slot-scope="scope">
            <span>{{ scope.row.stockSold }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('fabricStockReport.reservedStock')" property="reservedStock">
          <template slot-scope="scope">
            <span>{{ scope.row.stockReserved }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('fabricStockReport.blockedStock')" property="blockedStock">
          <template slot-scope="scope">
            <span>{{ scope.row.stockBlocked }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div style="margin-top: 20px">
        <el-button v-loading="loading" :disabled="loading" type="primary" @click="exportToExcel(items)">
          {{ $t('common.downloadExcel') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import CrudTablePersistentState from '@/components/crud/CrudTablePersistentState';

const persistentState = new CrudTablePersistentState();

export default {
  name: 'StockReport',
  components: { Pagination },
  props: {
    stockLocationsToReport: {
      type: Array,
      default: () => []
    },
    fabricStatusOptionsSelected: {
      type: Array,
      default: () => []
    },
    includeAvailableAmount: {
      type: Boolean
    },
    includeReservedAmount: {
      type: Boolean
    },
    includeBlockedAmount: {
      type: Boolean
    },
    includePositionCode: {
      type: Boolean
    },
    value: {
      type: Object,
      default: () => {}
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      items: [],
      filteredItems: [],
      paginatedItems: [],
      numberOfItems: 0,
      loading: false,
      defaultCellAlign: 'left',
      listQuery: {
        limit: 50,
        page: 1
      },
      search: persistentState.getDefaultSearch(this.filters)
    };
  },
  computed: {
    dateTimeFormat() {
      return `${this.$store.getters.dateFormat.toUpperCase()} ${this.$store.getters.timeFormat}`;
    },
    listParams() {
      const params = {};
      params.query = { ...this.filters, ...this.listQuery };
      if (Object.keys(this.search).length > 0) {
        params.query = { ...params.query, ...this.search };
      }
      return params;
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.listQuery.page = 1;
        this.filterAllItems();
        this.numberOfItems = this.filteredItems.length;
        this.paginateList({ limit: this.listQuery.limit, page: 1 });
      }
    },
    value(value) {
      this.items = value.items;
      this.filterAllItems();
      this.numberOfItems = this.filteredItems.length;
      this.paginateList({ limit: this.listQuery.limit, page: 1 });
    }
  },
  methods: {
    filterAllItems() {
      let filteredItems = [...this.items];
      if (this.search.description && this.search.description.length) {
        filteredItems = filteredItems.filter(
          e => !!e.description && e.description.toLowerCase().indexOf(this.search.description.toLowerCase()) !== -1
        );
      }
      if (this.search.domainProductCode && this.search.domainProductCode.length) {
        filteredItems = filteredItems.filter(e => !!e.code && e.code.indexOf(this.search.domainProductCode) !== -1);
      }
      if (this.search.domainPrefix && this.search.domainPrefix.length) {
        filteredItems = filteredItems.filter(
          e => !!e.domainPrefix && e.domainPrefix.toLowerCase().indexOf(this.search.domainPrefix.toLowerCase()) !== -1
        );
      }
      this.filteredItems = filteredItems;
    },
    getFabricStatusOptionFromRow(status) {
      const option = this.$store.getters['option/loadedOptions']('fabricStatus').find(item => item.code === status);
      return option && option.description;
    },
    getAvailableStockFromRow(stockLocationsInRow, id) {
      const stockLocation = stockLocationsInRow.find(w => w.stockLocationID === id);
      if (stockLocation) {
        return stockLocation.availableStock;
      } else {
        return '';
      }
    },
    getReservedStockFromRow(stockLocationsInRow, id) {
      const stockLocation = stockLocationsInRow.find(w => w.stockLocationID === id);
      if (stockLocation) {
        return stockLocation.reservedStock;
      } else {
        return '';
      }
    },
    getBlockedStockFromRow(stockLocationsInRow, id) {
      const stockLocation = stockLocationsInRow.find(w => w.stockLocationID === id);
      if (stockLocation) {
        return stockLocation.blockedStock;
      } else {
        return '';
      }
    },
    getSoldStockFromRow(stockLocationsInRow, id) {
      const stockLocation = stockLocationsInRow.find(w => w.stockLocationID === id);
      if (stockLocation) {
        return stockLocation.soldStock;
      } else {
        return '';
      }
    },
    getPositionCodeFromRow(stockLocationsInRow, id) {
      const stockLocation = stockLocationsInRow.find(w => w.stockLocationID === id);
      if (stockLocation) {
        return stockLocation.positionCode;
      } else {
        return '';
      }
    },
    async exportToExcel() {
      const itemsToExport = this.filteredItems;
      const headers = this.stockLocationsToReport.map(stockLocation => [
        ...(this.includeAvailableAmount
          ? [stockLocation.name + ' ' + this.$i18n.t('fabricStockReport.availableStock')]
          : []),
        ...(this.includeReservedAmount
          ? [stockLocation.name + ' ' + this.$i18n.t('fabricStockReport.reservedStock')]
          : []),
        ...(this.includeBlockedAmount
          ? [stockLocation.name + ' ' + this.$i18n.t('fabricStockReport.blockedStock')]
          : []),
        ...(this.includePositionCode ? [stockLocation.name + ' ' + this.$i18n.t('stock.positionCode')] : [])
      ]);

      const dataColumns = this.stockLocationsToReport.map(stockLocation => [
        ...(this.includeAvailableAmount ? [{ id: stockLocation.id, prop: 'availableStock' }] : []),
        ...(this.includeReservedAmount ? [{ id: stockLocation.id, prop: 'reservedStock' }] : []),
        ...(this.includeBlockedAmount ? [{ id: stockLocation.id, prop: 'blockedStock' }] : []),
        ...(this.includePositionCode ? [{ id: stockLocation.id, prop: 'positionCode' }] : [])
      ]);

      itemsToExport.map(i => {
        i.stockData = dataColumns.flat().map(x => {
          const stockForLocations = i.stockForStockLocations.find(s => s.stockLocationID === x.id);
          return stockForLocations && stockForLocations[x.prop];
        });
      });
      this.loading = true;
      import('@/vendor/Export2Excel').then(excel => {
        const header = [
          this.$i18n.t('common.domain'),
          this.$i18n.t('fabric.domainProductCode'),
          this.$i18n.t('fabric.description'),
          this.$i18n.t('fabric.yarn'),
          this.$i18n.t('fabric.fabricStatusOption'),
          this.$i18n.t('fabricStockReport.totalStock'),
          ...headers.flat(),
          this.$i18n.t('fabricStockReport.availableStock'),
          this.$i18n.t('fabricStockReport.soldStock'),
          this.$i18n.t('fabricStockReport.reservedStock'),
          this.$i18n.t('fabricStockReport.blockedStock')
        ];
        const data = itemsToExport.map(i => [
          i.domainPrefix,
          i.code,
          i.description,
          i.yarnDescription,
          this.getFabricStatusOptionFromRow(i.status),
          i.stockTotal,
          ...i.stockData,
          i.stockAvailable,
          i.stockSold,
          i.stockReserved,
          i.stockBlocked
        ]);
        excel.export_json_to_excel({
          header: header,
          data,
          filename: this.$i18n.t('fabricStockReport.excelFileName'),
          autoWidth: true,
          bookType: 'xlsx',
          worksheetName: this.$i18n.t('fabricStockReport.excelWorksheetName')
        });
        this.loading = false;
      });
    },
    paginateList(pageInfo) {
      const startIndex = (this.listQuery.page - 1) * this.listQuery.limit;
      const endIndex = startIndex + this.listQuery.limit;
      this.paginatedItems = this.filteredItems.slice(startIndex, endIndex);
      this.listQuery.page = pageInfo.page;
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        const spaceForButtons = 150;
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - spaceForButtons;
      }
    }
  }
};
</script>
